import {
  DEFAULT_TIME_ZONE,
  fromDateToTemporalInstant,
  fromIsoStringToTemporalInstant
} from '@miles/prelude/temporal'
import {
  differenceInYears,
  format,
  isAfter,
  isBefore,
  isValid,
  parse
} from 'date-fns'

export const formatDateWithTime = (date: Date, locale: string) =>
  date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })

export const getTimeForTimezone = (
  date: Date,
  timeZone: string | null,
  locale: string
): string =>
  date.toLocaleTimeString(locale, {
    timeZone: timeZone ?? DEFAULT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

export const getDateForTimezone = (
  date: Date,
  timeZone: string | null,
  locale: string,
  intl: Intl.DateTimeFormatOptions
): string =>
  date.toLocaleDateString(locale, {
    timeZone: timeZone ?? DEFAULT_TIME_ZONE,
    ...intl
  })

export const getHoursForTimezone = (
  date: Date,
  timeZone: string | null
): number =>
  Number(
    date.toLocaleTimeString('en', {
      timeZone: timeZone ?? DEFAULT_TIME_ZONE,
      hour: '2-digit',
      hour12: false
    })
  )

export const getMinutesForTimezone = (
  date: Date,
  timeZone: string | null
): number =>
  Number(
    date.toLocaleTimeString('en', {
      timeZone: timeZone ?? DEFAULT_TIME_ZONE,
      minute: '2-digit'
    })
  )

export const getRelativeAgeFromBirthDate = (
  birthDate: Date,
  fromDate: Date = new Date()
): number =>
  new Date(fromDate).getFullYear() - new Date(birthDate).getFullYear()

export const getAgeFromBirthDate = (
  birthDate: Date,
  fromDate: Date = new Date()
): number => differenceInYears(fromDate, birthDate)

export const isMinor = (birthDate: Date, fromDate?: Date): boolean =>
  getAgeFromBirthDate(birthDate, fromDate) < 18

export const displayDate = (
  date: Date | string,
  locale: string,
  timeZone: string = 'Europe/Paris'
): string => {
  const TemporalDate =
    typeof date === 'string'
      ? fromIsoStringToTemporalInstant(date).toZonedDateTimeISO(timeZone)
      : isValid(date)
      ? fromDateToTemporalInstant(date).toZonedDateTimeISO(timeZone)
      : null
  if (!TemporalDate) {
    return ''
  }

  return TemporalDate.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}
export const displayDateWithTime = (
  date: Date | string | null,
  locale: string | undefined,
  timeZone: string
): string => {
  if (!date) {
    return '-'
  }

  const TemporalDate =
    typeof date === 'string'
      ? fromIsoStringToTemporalInstant(date).toZonedDateTimeISO(timeZone)
      : fromDateToTemporalInstant(date).toZonedDateTimeISO(timeZone)

  return TemporalDate.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const parseFrenchDate = (frenchDate: string): Date => {
  return parse(`${frenchDate}Z`, 'dd/MM/yyyyX', new Date())
}

export const parseDatabaseDate = (databaseDate: string): Date => {
  return parse(databaseDate, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date())
}

export const formatDateForExport = ({
  date,
  withHour = false
}: {
  date: Date
  withHour?: boolean
}): string => format(date, withHour ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd')

export const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date)
  newDate.setDate(date.getDate() + days)
  return newDate
}

export const addMinutes = (date: Date, minutes: number): Date => {
  const newDate = new Date(date)
  newDate.setMinutes(date.getMinutes() + minutes)
  return newDate
}

export const getMinDate = (firstDate: Date | null, secondDate: Date | null) => {
  if (!firstDate || !secondDate) {
    return null
  }
  return firstDate < secondDate ? firstDate : secondDate
}

export const getMaxDate = (firstDate: Date | null, secondDate: Date | null) => {
  if (!firstDate || !secondDate) {
    return null
  }
  return firstDate > secondDate ? firstDate : secondDate
}

export const isBetweenInclusive = (begin: Date, end: Date) => (date: Date) =>
  !isBefore(date, begin) && !isAfter(date, end)

export const dateWithHoursFormat: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  day: 'numeric',
  weekday: 'long',
  month: 'long',
  year: 'numeric'
}

const dateFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
}

const hoursFormat: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric'
}

export const formatEditionDateWithHours =
  (locale: string) =>
  (date: Date | null): [string, string] | null => {
    if (!date) {
      return null
    }

    return [
      date.toLocaleDateString(locale, dateFormat),
      date.toLocaleTimeString(locale, hoursFormat)
    ]
  }
